import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
} from "react-bootstrap";

const UploadBatchModal = (props) => {
  const usersOnly = !!props.usersOnly;
  const singleLead = !!props.singleLead;
  const sampleCsv = "lead_id,remarks (optional)\r\n";
  const sampleCsvHref = "data:text/csv;charset=utf-8," + encodeURIComponent(sampleCsv);
  const [userSearch, setUserSearch] = useState("");
  useEffect(() => {
    if (!props.show) setUserSearch("");
  }, [props.show]);
  const visibleUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return [];
    return (props.users || []).filter((user) =>
      (user.name || "").toLowerCase().includes(term) ||
      (user.email || "").toLowerCase().includes(term),
    );
  }, [props.users, userSearch]);
  const selectedUsers = useMemo(() => {
    const selectedIds = (props.uploadUsers || []).map(String);
    return (props.users || []).filter((user) => selectedIds.includes(String(user.id)));
  }, [props.users, props.uploadUsers]);
  return (
    <Modal show={props.show} onHide={props.onHide} backdrop="static">
      <form onSubmit={props.onUpload} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>
            {singleLead ? "Assign lead" : usersOnly ? "Assign batch users" : "Upload call batch"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {singleLead || usersOnly ? (
            <p className="cw-modal-help">
              {singleLead
                ? `Choose a caller for “${props.leadName}” in “${props.batchName}”.`
                : "Select the callers who should work on “" +
                  props.batchName +
                  "”. Leads will be assigned equally across the selected users."}
            </p>
          ) : (
            null
          )}
          {!usersOnly && !singleLead ? (
            <React.Fragment>
              <FormGroup>
                <ControlLabel>Batch name</ControlLabel>
                <FormControl
                  name="name"
                  value={props.upload.name}
                  onChange={props.onUploadChange}
                  maxLength={150}
                />
              </FormGroup>
              <FormGroup>
                <ControlLabel>Campaign date</ControlLabel>
                <FormControl
                  type="date"
                  name="campaignDate"
                  value={props.upload.campaignDate}
                  onChange={props.onUploadChange}
                />
              </FormGroup>
              <FormGroup>
                <ControlLabel>Call type</ControlLabel>
                <FormControl
                  componentClass="select"
                  name="callTypeId"
                  value={props.upload.callTypeId || ""}
                  onChange={props.onUploadChange}
                  disabled={props.uploading || !!props.callTypesError || !(props.callTypes || []).length}
                >
                  <option value="">Select a call type</option>
                  {(props.callTypes || []).map((type) => (
                    <option key={type.id} value={String(type.id)}>{type.name}</option>
                  ))}
                </FormControl>
                {props.batchCallType ? (
                  <p className="cw-empty-help">Batch is selected by default. You can choose another call type.</p>
                ) : null}
                {props.callTypesError ? (
                  <div className="cw-tab-error" role="alert">
                    {props.callTypesError} <Button type="button" bsStyle="link" onClick={props.onRetryCallTypes}>Retry</Button>
                  </div>
                ) : null}
                {!props.callTypesError && !(props.callTypes || []).length ? (
                  <p className="cw-empty-help">No active call types are available. Create or activate a call type first.</p>
                ) : null}
              </FormGroup>
              <FormGroup>
                <div className="cw-upload-file-label">
                  <ControlLabel>CSV or Excel File</ControlLabel>
                  <a
                    className="cw-sample-download"
                    href={sampleCsvHref}
                    download="call-workspace-batch-sample.csv"
                  >
                    Sample CSV
                  </a>
                </div>
                <FormControl
                  type="file"
                  name="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={props.onUploadChange}
                />
              </FormGroup>
            </React.Fragment>
          ) : null}
          <div className="cw-upload-assignees">
            <ControlLabel>{singleLead ? "Assign caller" : "Assign users equally"}</ControlLabel>
            <FormControl
              type="search"
              value={userSearch}
              onChange={(event) => setUserSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
              placeholder="Type a name or email to search callers"
              aria-label="Search users"
            />
            {singleLead && !userSearch.trim() ? (
              <p className="cw-empty-help">
                {props.currentAssigneeName
                  ? `Currently assigned to ${props.currentAssigneeName}. Type to search for a caller.`
                  : "Start typing a caller’s name or email to search."}
              </p>
            ) : null}
            {!singleLead && !userSearch.trim() ? (
              <React.Fragment>
                <p className="cw-empty-help">Start typing a caller’s name or email to see matching users.</p>
                {selectedUsers.length ? (
                  <div className="cw-selected-callers" aria-label="Selected callers">
                    <small>Selected callers</small>
                    {selectedUsers.map((user) => (
                      <div className="cw-selected-caller" key={user.id}>
                        <span>
                          <strong>{user.name}</strong>
                          <small>{Number(user.active_calls_count || 0)} active calls to make</small>
                        </span>
                        <Button
                          type="button"
                          bsStyle="link"
                          aria-label={`Remove ${user.name}`}
                          onClick={() => props.onToggleUploadUser(user.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </React.Fragment>
            ) : null}
            {props.usersLoading ? <p className="cw-muted">Loading callers…</p> : null}
            {props.usersError ? (
              <div className="cw-tab-error" role="alert">
                {props.usersError} <Button type="button" bsStyle="link" onClick={props.onRetryUsers}>Retry loading callers</Button>
              </div>
            ) : null}
            {userSearch.trim() && !props.usersLoading && !props.usersError && !visibleUsers.length ? (
              <p className="cw-empty-help">No callers match that search.</p>
            ) : null}
            {visibleUsers.map((user) => (
              <label key={user.id} className="cw-user-option">
                <input
                  type={singleLead ? "radio" : "checkbox"}
                  name={singleLead ? "call-workspace-lead-assignee" : undefined}
                  checked={singleLead
                    ? String(props.selectedUserId || "") === String(user.id)
                    : props.uploadUsers.indexOf(user.id) !== -1}
                  onChange={() => singleLead
                    ? props.onSelectUser(String(user.id))
                    : props.onToggleUploadUser(user.id)}
                />
                <span>
                  <strong>{user.name}</strong>
                  <small>
                    {Number(user.active_calls_count || 0)} active calls to make
                  </small>
                </span>
              </label>
            ))}
            {singleLead && props.currentAssigneeId ? (
              <label className="cw-user-option">
                <input
                  type="radio"
                  name="call-workspace-lead-assignee"
                  checked={!props.selectedUserId}
                  onChange={() => props.onSelectUser("")}
                />
                <span>
                  <strong>Unassign lead</strong>
                  <small>Remove the current caller assignment</small>
                </span>
              </label>
            ) : null}
            {!singleLead && !props.uploadUsers.length ? (
              <p className="cw-empty-help">Select at least one caller.</p>
            ) : null}
          </div>
          {singleLead && props.assignmentError ? <div className="cw-tab-error" role="alert">{props.assignmentError}</div> : null}
          {!singleLead && props.error ? <div className="cw-tab-error" role="alert">{props.error}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            onClick={props.onHide}
            disabled={props.uploading || props.assigning}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            bsStyle="primary"
            disabled={props.uploading || props.assigning || props.usersLoading || !!props.usersError || (!usersOnly && !singleLead && (!props.upload.callTypeId || !!props.callTypesError || !(props.callTypes || []).length)) || (singleLead && String(props.selectedUserId || "") === String(props.currentAssigneeId || "")) || (usersOnly && !props.uploadUsers.length)}
          >
            {singleLead
              ? props.assigning ? "Assigning…" : "Assign lead"
              : usersOnly
              ? props.assigning ? "Assigning…" : "Assign users"
              : props.uploading
                ? "Uploading…"
                : "Upload batch"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
export default UploadBatchModal;
